/**
 * Страница сравнения двух SHA одного pflow-репозитория.
 *
 * URL: /r/:owner/:repo/compare?baseRef=<ref>&headRef=<ref>&base=<sha>&head=<sha>&path=<subpath>
 *
 * Поток:
 *   1. Загружаем ветки и PR-ы через /refs.
 *   2. baseRef по умолчанию = defaultBranch, headRef = undefined (пользователь выбирает).
 *   3. Для каждой ветки грузим независимый список коммитов (/commits?ref=...).
 *   4. Два таймлайна рядом — левый для Base, правый для Head.
 *      Клик «Base» в левом устанавливает base-SHA, «Head» в правом — head-SHA.
 *   5. Быстрый выбор PR-а сразу заполняет обе ветки (defaultBranch → prRef).
 *   6. Готов — жмём «Сравнить», запускается diff.
 */

import {
    Accordion,
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Center,
    Container,
    Divider,
    Group,
    Loader,
    Select,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
    fetchCommits,
    fetchMergeBase,
    fetchRefs,
    fetchRepoDiff,
    type CommitInfo,
    type RepoDiff,
} from '../lib/api-client';
import { EntityDiffCard } from '../components/EntityDiffCard';
import { registerChatContext } from '../stores/chat-store';
import type { ChatPageContext } from '@pflow/shared/v2';

export function ComparePage(): JSX.Element {
    const { owner, repo } = useParams<{ owner: string; repo: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const baseRef  = searchParams.get('baseRef')  ?? undefined;
    const headRef  = searchParams.get('headRef')  ?? undefined;
    const base     = searchParams.get('base')     ?? undefined;
    const head     = searchParams.get('head')     ?? undefined;
    const path     = searchParams.get('path')     ?? undefined;

    const refsQ = useQuery({
        queryKey: ['refs', owner, repo],
        queryFn:  () => fetchRefs({ owner: owner!, repo: repo! }),
        enabled:  !!owner && !!repo,
        staleTime: 120_000,
    });

    // Как только узнаём defaultBranch — выставляем baseRef в URL (один раз).
    useEffect(() => {
        if (refsQ.data && !baseRef) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('baseRef', refsQ.data.defaultBranch);
                return next;
            }, { replace: true });
        }
    }, [refsQ.data, baseRef, setSearchParams]);

    // Фильтр таймлайна по .pflow-коммитам имеет смысл только когда сравниваем
    // две точки ОДНОЙ ветки (baseRef === headRef). При разных ветках/PR история
    // PR-ветки может почти не трогать .pflow, и отфильтрованный список пуст —
    // выбрать реальную голову ветки нельзя. Тогда показываем все коммиты.
    const allCommits = !!baseRef && !!headRef && baseRef !== headRef;

    const baseCommitsQ = useQuery({
        queryKey: ['compare-commits', owner, repo, baseRef, path, allCommits],
        queryFn:  () => fetchCommits({ owner: owner!, repo: repo!, ref: baseRef, path, limit: 50, all: allCommits }),
        enabled:  !!owner && !!repo && !!baseRef,
        staleTime: 60_000,
    });

    const headCommitsQ = useQuery({
        queryKey: ['compare-commits', owner, repo, headRef, path, allCommits],
        queryFn:  () => fetchCommits({ owner: owner!, repo: repo!, ref: headRef, path, limit: 50, all: allCommits }),
        enabled:  !!owner && !!repo && !!headRef,
        staleTime: 60_000,
    });

    // Точка расхождения веток (merge-base) — естественный base для сравнения
    // PR. Считаем только при разных ветках.
    const mergeBaseQ = useQuery({
        queryKey: ['merge-base', owner, repo, baseRef, headRef],
        queryFn:  () => fetchMergeBase({ owner: owner!, repo: repo!, base: baseRef!, head: headRef! }),
        enabled:  !!owner && !!repo && !!baseRef && !!headRef && baseRef !== headRef,
        staleTime: 60_000,
    });

    const [started, setStarted] = useState(!!base && !!head);

    // После выбора PR base+head подбираются из свежих таймлайнов обеих веток,
    // как только они загрузятся: новейший .pflow-коммит каждой ветки = её
    // текущий снимок. Берём SHA из /commits (а не pr.sha) — он гарантированно
    // фетчится сервером и подсвечивается в таймлайне.
    const [pendingPrAutofill, setPendingPrAutofill] = useState(false);

    const diffQ = useQuery({
        queryKey: ['compare-diff', owner, repo, base, head, path],
        queryFn:  () => fetchRepoDiff({
            owner: owner!, repo: repo!,
            baseSha: base!, headSha: head!,
            path,
        }),
        enabled:  !!owner && !!repo && !!base && !!head && base !== head && started,
        retry:    false,
    });

    // Дозаполняем base+head после выбора PR. head — верхушка head-ветки.
    // base при разных ветках — merge-base (точка расхождения): ждём его и
    // откатываемся на верхушку base-ветки, только если merge-base не нашёлся.
    // При одной ветке base — просто новейший коммит base-ветки.
    // Каждую сторону заполняем независимо; флаг снимаем, когда обе заполнены.
    useEffect(() => {
        if (!pendingPrAutofill) return;

        let nextBase = base;
        if (!nextBase) {
            if (allCommits) {
                // merge-base ещё считается — подождём следующего прогона.
                if (!mergeBaseQ.isLoading) {
                    nextBase = mergeBaseQ.data?.sha ?? baseCommitsQ.data?.commits[0]?.sha;
                }
            } else {
                nextBase = baseCommitsQ.data?.commits[0]?.sha;
            }
        }
        const nextHead = head || headCommitsQ.data?.commits[0]?.sha;

        if (nextBase !== base || nextHead !== head) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                if (nextBase) next.set('base', nextBase);
                if (nextHead) next.set('head', nextHead);
                return next;
            });
        }
        if (nextBase && nextHead) setPendingPrAutofill(false);
    }, [pendingPrAutofill, base, head, allCommits, mergeBaseQ.isLoading, mergeBaseQ.data, baseCommitsQ.data, headCommitsQ.data, setSearchParams]);

    const dateByCommit = useMemo(() => {
        const map = new Map<string, string>();
        for (const c of baseCommitsQ.data?.commits ?? []) map.set(c.sha, c.date);
        for (const c of headCommitsQ.data?.commits  ?? []) map.set(c.sha, c.date);
        return map;
    }, [baseCommitsQ.data, headCommitsQ.data]);

    if (!owner || !repo) {
        return <Center mih="60vh"><Text c="dimmed">Нет owner/repo в URL.</Text></Center>;
    }

    function setParams(updates: Record<string, string | undefined>): void {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            for (const [k, v] of Object.entries(updates)) {
                if (v !== undefined) next.set(k, v);
                else next.delete(k);
            }
            return next;
        });
    }

    function applyPicker(nextBase?: string, nextHead?: string): void {
        let b = nextBase;
        let h = nextHead;
        // Авто-своп по дате имеет смысл только в пределах одной ветки. При
        // сравнении разных веток/PR голова PR может быть хронологически старше
        // тэка base-ветки, и своп всё поломал бы — base/head задаёт пользователь.
        if (!allCommits && b && h && b !== h) {
            const bDate = dateByCommit.get(b);
            const hDate = dateByCommit.get(h);
            if (bDate && hDate && bDate > hDate) [b, h] = [h, b];
        }
        setParams({ base: b, head: h });
        setStarted(false);
    }

    const branchOptions = (refsQ.data?.branches ?? []).map(b => ({ value: b, label: b }));
    const prList = refsQ.data?.prs ?? [];
    const baseDate = base ? dateByCommit.get(base) : undefined;
    const headDate = head ? dateByCommit.get(head) : undefined;
    const canCompare = !!base && !!head && base !== head;

    return (
        <Container size="xl" py="lg">
            <Group justify="space-between" mb="md">
                <Box>
                    <Text fz="sm" c="dimmed">
                        <Link to={`/r/${owner}/${repo}${path !== undefined ? `?path=${encodeURIComponent(path)}` : ''}`}>
                            ◄ {owner}/{repo}
                        </Link>
                    </Text>
                    <Title order={2} mt={4}>Сравнение версий</Title>
                </Box>
            </Group>

            <SelectionPanel
                base={base}
                head={head}
                baseRef={baseRef}
                headRef={headRef}
                started={started}
                canCompare={canCompare}
                onCompare={() => setStarted(true)}
                onEdit={() => setStarted(false)}
                onClear={() => { setParams({ base: undefined, head: undefined }); setStarted(false); }}
            />

            {!started && (
                <>
                    {/* PR быстрый выбор */}
                    {prList.length > 0 && (
                        <Card withBorder mb="md">
                            <Text fz="xs" tt="uppercase" c="dimmed" mb="xs">Открытые PR</Text>
                            <Stack gap="xs">
                                {prList.map(pr => (
                                    <Group key={pr.number} justify="space-between" wrap="nowrap">
                                        <Box style={{ minWidth: 0, flex: 1 }}>
                                            <Text fz="sm" lineClamp={1}>
                                                <Text span c="dimmed" ff="monospace">#{pr.number}</Text>
                                                {' '}{pr.title}
                                            </Text>
                                            <Text fz="xs" c="dimmed">{pr.ref}</Text>
                                        </Box>
                                        <Button
                                            size="xs"
                                            variant="light"
                                            onClick={() => {
                                                const defaultBranch = refsQ.data?.defaultBranch ?? 'main';
                                                setParams({
                                                    baseRef: defaultBranch,
                                                    headRef: pr.ref,
                                                    base: undefined,   // подберутся из таймлайнов веток
                                                    head: undefined,
                                                });
                                                setPendingPrAutofill(true);
                                                setStarted(false);
                                            }}
                                        >
                                            Выбрать
                                        </Button>
                                    </Group>
                                ))}
                            </Stack>
                        </Card>
                    )}

                    {/* Выбор веток */}
                    <Group align="flex-start" grow mb="md">
                        <Box>
                            <Text fz="xs" tt="uppercase" c="dimmed" mb={4}>Base ветка</Text>
                            <Select
                                data={branchOptions}
                                value={baseRef ?? null}
                                onChange={v => setParams({ baseRef: v ?? undefined, base: undefined })}
                                placeholder="Выбери ветку…"
                                searchable
                                disabled={refsQ.isLoading}
                            />
                        </Box>
                        <Box>
                            <Text fz="xs" tt="uppercase" c="dimmed" mb={4}>Head ветка</Text>
                            <Select
                                data={branchOptions}
                                value={headRef ?? null}
                                onChange={v => setParams({ headRef: v ?? undefined, head: undefined })}
                                placeholder="Выбери ветку…"
                                searchable
                                disabled={refsQ.isLoading}
                            />
                        </Box>
                    </Group>

                    {/* Два таймлайна рядом */}
                    <Group align="flex-start" grow gap="md">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text fz="sm" fw={600} mb="xs" c="blue">Base: {baseRef ?? '—'}</Text>
                            {!baseRef && <Text c="dimmed" fz="sm">Выбери Base ветку выше.</Text>}
                            {baseRef && baseCommitsQ.isLoading && <Center my="md"><Loader size="sm" /></Center>}
                            {baseRef && baseCommitsQ.error && (
                                <Alert color="red" fz="sm">{(baseCommitsQ.error as Error).message}</Alert>
                            )}
                            {baseRef && baseCommitsQ.data && (
                                <CommitTimeline
                                    commits={baseCommitsQ.data.commits}
                                    base={base}
                                    head={undefined}
                                    baseDate={baseDate}
                                    headDate={headDate}
                                    side="base"
                                    allMode={allCommits}
                                    onPick={(b, h) => applyPicker(b ?? base, h ?? head)}
                                />
                            )}
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text fz="sm" fw={600} mb="xs" c="green">Head: {headRef ?? '—'}</Text>
                            {!headRef && <Text c="dimmed" fz="sm">Выбери Head ветку выше.</Text>}
                            {headRef && headCommitsQ.isLoading && <Center my="md"><Loader size="sm" /></Center>}
                            {headRef && headCommitsQ.error && (
                                <Alert color="red" fz="sm">{(headCommitsQ.error as Error).message}</Alert>
                            )}
                            {headRef && headCommitsQ.data && (
                                <CommitTimeline
                                    commits={headCommitsQ.data.commits}
                                    base={undefined}
                                    head={head}
                                    baseDate={baseDate}
                                    headDate={headDate}
                                    side="head"
                                    allMode={allCommits}
                                    onPick={(b, h) => applyPicker(b ?? base, h ?? head)}
                                />
                            )}
                        </Box>
                    </Group>
                </>
            )}

            <Divider my="lg" />

            {!started && (!base || !head) && (
                <Text c="dimmed" fz="sm">
                    Выбери коммит <strong>Base</strong> (слева) и <strong>Head</strong> (справа), затем жми «Сравнить».
                </Text>
            )}
            {!started && canCompare && (
                <Text c="dimmed" fz="sm">Готов сравнить. Жми <strong>Сравнить</strong> в шапке.</Text>
            )}
            {started && base && head && base === head && (
                <Alert color="yellow">Base и Head совпадают — сравнивать нечего.</Alert>
            )}
            {started && canCompare && diffQ.isLoading && (
                <Stack align="center" my="xl">
                    <Loader />
                    <Text c="dimmed" fz="sm">
                        Готовлю diff: клонирую обе версии, собираю snapshot'ы, генерирую narrative.
                        Cold-path первый раз может занять 30-90 секунд.
                    </Text>
                </Stack>
            )}
            {started && diffQ.error && (
                <Alert color="red" title="Ошибка сравнения">
                    {(diffQ.error as Error).message}
                </Alert>
            )}
            {started && diffQ.data && <DiffView diff={diffQ.data} />}

            {/* Контекст глобального чата: только когда diff загружен — иначе чат
                не знает, что именно сравнивается (tool'ы pflow_diff_*). */}
            <CompareChatContext
                active={!!(started && canCompare && diffQ.data)}
                owner={owner} repo={repo} gitRef={headRef} path={path}
                base={base} head={head} baseRef={baseRef} headRef={headRef}
            />
        </Container>
    );
}

// ─── selection summary ────────────────────────────────────────────────

function SelectionPanel({
    base, head, baseRef, headRef, started, canCompare, onCompare, onEdit, onClear,
}: {
    base?:      string;
    head?:      string;
    baseRef?:   string;
    headRef?:   string;
    started:    boolean;
    canCompare: boolean;
    onCompare:  () => void;
    onEdit:     () => void;
    onClear:    () => void;
}): JSX.Element {
    return (
        <Card withBorder mb="md">
            <Group justify="space-between">
                <Group>
                    <Box>
                        <Text fz="xs" c="dimmed" tt="uppercase">Base</Text>
                        {baseRef && <Text fz="xs" c="blue">{baseRef}</Text>}
                        {base ? <Text ff="monospace">{base.slice(0, 7)}</Text> : <Text c="dimmed" fz="sm">не выбран</Text>}
                    </Box>
                    <Text c="dimmed">→</Text>
                    <Box>
                        <Text fz="xs" c="dimmed" tt="uppercase">Head</Text>
                        {headRef && <Text fz="xs" c="green">{headRef}</Text>}
                        {head ? <Text ff="monospace">{head.slice(0, 7)}</Text> : <Text c="dimmed" fz="sm">не выбран</Text>}
                    </Box>
                </Group>
                <Group gap="xs">
                    {started ? (
                        <Button size="sm" variant="default" onClick={onEdit}>Изменить</Button>
                    ) : (
                        <Button size="sm" disabled={!canCompare} onClick={onCompare}>Сравнить</Button>
                    )}
                    {(base || head) && (
                        <Button size="sm" variant="subtle" color="gray" onClick={onClear}>Сбросить</Button>
                    )}
                </Group>
            </Group>
        </Card>
    );
}

// ─── timeline picker ──────────────────────────────────────────────────

function CommitTimeline({
    commits, base, head, baseDate, headDate, side, allMode, onPick,
}: {
    commits:   CommitInfo[];
    base?:     string;
    head?:     string;
    baseDate?: string;
    headDate?: string;
    side:      'base' | 'head';
    allMode:   boolean;
    onPick:    (base?: string, head?: string) => void;
}): JSX.Element {
    if (commits.length === 0) {
        return <Text c="dimmed" fz="sm">{allMode ? 'Нет коммитов в проекте.' : 'Нет коммитов, затронувших .pflow/.'}</Text>;
    }
    return (
        <Stack gap={2}>
            {commits.map(c => {
                const isBase = !!base && (c.sha === base || c.sha.startsWith(base) || base.startsWith(c.sha.slice(0, 7)));
                const isHead = !!head && (c.sha === head || c.sha.startsWith(head) || head.startsWith(c.sha.slice(0, 7)));
                // Дисейбл по дате (head не старше base) — только в пределах одной
                // ветки. Между ветками порядок дат не отражает порядок сравнения.
                const baseDisabled = !allMode && !isBase && !!headDate && c.date > headDate;
                const headDisabled = !allMode && !isHead && !!baseDate && c.date < baseDate;
                return (
                    <CommitRow
                        key={c.sha}
                        commit={c}
                        isBase={isBase}
                        isHead={isHead}
                        baseDisabled={baseDisabled}
                        headDisabled={headDisabled}
                        showBase={side === 'base'}
                        showHead={side === 'head'}
                        onPickBase={() => onPick(c.sha, head)}
                        onPickHead={() => onPick(base, c.sha)}
                    />
                );
            })}
        </Stack>
    );
}

function CommitRow({
    commit, isBase, isHead, baseDisabled, headDisabled, showBase, showHead, onPickBase, onPickHead,
}: {
    commit:       CommitInfo;
    isBase:       boolean;
    isHead:       boolean;
    baseDisabled: boolean;
    headDisabled: boolean;
    showBase:     boolean;
    showHead:     boolean;
    onPickBase:   () => void;
    onPickHead:   () => void;
}): JSX.Element {
    const date = commit.date.slice(0, 10);
    return (
        <Card
            withBorder
            padding="xs"
            style={{
                borderColor: isBase ? 'var(--mantine-color-blue-5)'
                          : isHead ? 'var(--mantine-color-green-5)'
                          : undefined,
            }}
        >
            <Group justify="space-between" wrap="nowrap">
                <Box style={{ minWidth: 0, flex: 1 }}>
                    <Group gap="xs" wrap="nowrap">
                        <Text ff="monospace" fz="xs" c="dimmed">{commit.sha.slice(0, 7)}</Text>
                        <Text fz="xs" c="dimmed">{date}</Text>
                        <Text fz="xs" c="dimmed">{commit.author}</Text>
                    </Group>
                    <Text fz="sm" lineClamp={1}>{commit.message}</Text>
                </Box>
                <Group gap="xs" wrap="nowrap">
                    {isBase && <Badge color="blue" size="xs">Base</Badge>}
                    {isHead && <Badge color="green" size="xs">Head</Badge>}
                    {showBase && (
                        <Button
                            size="xs"
                            variant={isBase ? 'filled' : 'light'}
                            color="blue"
                            onClick={onPickBase}
                            disabled={isBase || baseDisabled}
                            title={baseDisabled ? 'Base должен быть старше Head' : undefined}
                        >
                            Base
                        </Button>
                    )}
                    {showHead && (
                        <Button
                            size="xs"
                            variant={isHead ? 'filled' : 'light'}
                            color="green"
                            onClick={onPickHead}
                            disabled={isHead || headDisabled}
                            title={headDisabled ? 'Head должен быть новее Base' : undefined}
                        >
                            Head
                        </Button>
                    )}
                </Group>
            </Group>
        </Card>
    );
}

// ─── diff view ────────────────────────────────────────────────────────

function DiffView({ diff }: { diff: RepoDiff }): JSX.Element {
    const changed  = useMemo(() => diff.aspectDeltas.filter(a => a.changed), [diff]);
    const health   = useMemo(() => diff.healthDeltas.filter(h => h.changed), [diff]);
    const entities = diff.entityDiffs ?? [];
    const diag     = diff.diagnostics;

    return (
        <Stack gap="lg">
            {diag && (diag.base.errors > 0 || diag.head.errors > 0) && (
                <Alert color="yellow" title="Парсер не смог обработать часть DSL">
                    <Text fz="sm">
                        Base: <strong>{diag.base.errors}</strong> ошибок парсинга,
                        {' '}Head: <strong>{diag.head.errors}</strong> ошибок.
                        {diag.base.errors > 5 && diag.head.errors === 0 && (
                            <> Скорее всего в base используется устаревший синтаксис —
                                часть сущностей в base не извлеклась, поэтому появились лишние «добавленные» в diff'е.</>
                        )}
                        {diag.head.errors > 5 && diag.base.errors === 0 && (
                            <> В head есть синтаксические ошибки — посмотри диагностики в редакторе.</>
                        )}
                    </Text>
                </Alert>
            )}
            <SummaryBar diff={diff} />
            {diff.rollUp && (
                <Card withBorder padding="md">
                    <Group gap="xs" mb="xs">
                        <Text fz="xs" tt="uppercase" c="dimmed">Главный сдвиг</Text>
                    </Group>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{diff.rollUp}</Text>
                </Card>
            )}

            <Box>
                <Title order={4} mb="xs">Изменения в сущностях ({entities.length})</Title>
                {entities.length === 0
                    ? <Text c="dimmed" fz="sm">DSL-блоки сущностей идентичны в обеих версиях.</Text>
                    : <Accordion variant="separated" multiple>
                        {entities.map(e => (
                            <EntityDiffCard
                                key={`${e.entity.type}::${e.entity.name}::${e.entity.canvas ?? ''}`}
                                diff={e}
                            />
                        ))}
                    </Accordion>
                }
            </Box>

            <Box>
                <Title order={4} mb="xs">Контекст в нарративах ({changed.length})</Title>
                {changed.length === 0
                    ? <Text c="dimmed" fz="sm">Ни один нарратив не изменился.</Text>
                    : <Accordion variant="separated" multiple>
                        {changed.map(a => (
                            <AspectDeltaCard key={`${a.id}#${a.scope.kind === 'project' ? 'project' : 'canvas:' + a.scope.canvas}`} delta={a} />
                        ))}
                    </Accordion>
                }
            </Box>

            {health.length > 0 && (
                <Box>
                    <Title order={4} mb="xs">Здоровье модели</Title>
                    <Stack gap="xs">
                        {health.map(h => <HealthDeltaCard key={h.id} delta={h} />)}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
}

function SummaryBar({ diff }: { diff: RepoDiff }): JSX.Element {
    const s = diff.summary;
    return (
        <Card withBorder padding="md">
            <Group gap="lg">
                <Stat label="Сущностей изменено" value={s.changedEntities ?? 0} />
                <Stat label="Добавлено" value={s.addedEntities} positive={s.addedEntities > 0} />
                <Stat label="Удалено" value={s.removedEntities} negative={s.removedEntities > 0} />
                <Stat label="Нарративов" value={s.changedAspects} />
                <Stat label="Новых health" value={s.newHealthFindings} negative={s.newHealthFindings > 0} />
                <Stat label="Решено health" value={s.resolvedHealthFindings} positive={s.resolvedHealthFindings > 0} />
            </Group>
        </Card>
    );
}

function Stat({ label, value, positive, negative }: { label: string; value: number; positive?: boolean; negative?: boolean }): JSX.Element {
    const color = positive ? 'green' : negative ? 'red' : undefined;
    return (
        <Box>
            <Text fz="xs" c="dimmed" tt="uppercase">{label}</Text>
            <Text fz="lg" fw={600} c={color}>{value}</Text>
        </Box>
    );
}

function AspectDeltaCard({ delta }: { delta: RepoDiff['aspectDeltas'][number] }): JSX.Element {
    const scopeLabel = delta.scope.kind === 'project' ? 'весь проект' : `Канвас «${delta.scope.canvas}»`;
    const added   = delta.entities.filter(e => e.side === 'added');
    const removed = delta.entities.filter(e => e.side === 'removed');
    const itemKey = `${delta.id}#${delta.scope.kind === 'project' ? 'project' : 'canvas:' + delta.scope.canvas}`;

    const presenceTag =
        !delta.presence.base ? <Badge size="xs" color="green">новое</Badge>
      : !delta.presence.head ? <Badge size="xs" color="red">убрано</Badge>
      : null;

    return (
        <Accordion.Item value={itemKey}>
            <Accordion.Control>
                <Group justify="space-between" wrap="nowrap">
                    <Box style={{ minWidth: 0 }}>
                        <Text fw={600}>{delta.title}</Text>
                        <Text fz="xs" c="dimmed">{scopeLabel}</Text>
                    </Box>
                    <Group gap="xs">
                        {presenceTag}
                        {added.length   > 0 && <Badge size="xs" color="green">+{added.length}</Badge>}
                        {removed.length > 0 && <Badge size="xs" color="red">-{removed.length}</Badge>}
                        {delta.fields.length > 0 && <Badge size="xs" color="yellow">~{delta.fields.length}</Badge>}
                        {delta.summaryChanged && <Badge size="xs" color="blue">summary</Badge>}
                    </Group>
                </Group>
            </Accordion.Control>
            <Accordion.Panel>
                <Stack gap="sm">
                    {delta.narrative && delta.narrative !== 'минор' && (
                        <Box>
                            <Text fz="xs" tt="uppercase" c="dimmed">Сдвиг</Text>
                            <Text style={{ whiteSpace: 'pre-wrap' }}>{delta.narrative}</Text>
                        </Box>
                    )}
                    {delta.summaryChanged && (
                        <SummaryDiff before={delta.summaryBefore} after={delta.summaryAfter} />
                    )}
                    {added.length > 0 && (
                        <Box>
                            <Text fz="xs" tt="uppercase" c="dimmed">Добавлено</Text>
                            <Stack gap={2}>
                                {added.map((e, i) => (
                                    <Text key={i} fz="sm">+ {e.entity.type} «{e.entity.name}»{e.entity.canvas ? ` (Канвас «${e.entity.canvas}»)` : ''}</Text>
                                ))}
                            </Stack>
                        </Box>
                    )}
                    {removed.length > 0 && (
                        <Box>
                            <Text fz="xs" tt="uppercase" c="dimmed">Удалено</Text>
                            <Stack gap={2}>
                                {removed.map((e, i) => (
                                    <Text key={i} fz="sm">- {e.entity.type} «{e.entity.name}»{e.entity.canvas ? ` (Канвас «${e.entity.canvas}»)` : ''}</Text>
                                ))}
                            </Stack>
                        </Box>
                    )}
                    {delta.fields.length > 0 && (
                        <Box>
                            <Text fz="xs" tt="uppercase" c="dimmed">Изменения полей</Text>
                            <Stack gap={2}>
                                {delta.fields.map((f, i) => (
                                    <Text key={i} fz="sm">
                                        {f.entity.type} «{f.entity.name}».{f.key}: <Text span c="red">{fmt(f.before)}</Text> → <Text span c="green">{fmt(f.after)}</Text>
                                    </Text>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
}

function fmt(v: string | number | boolean | null): string {
    if (v === null)            return '(пусто)';
    if (typeof v === 'string') return v.length > 60 ? `${v.slice(0, 57)}…` : v;
    return String(v);
}

function SummaryDiff({ before, after }: { before: string | null; after: string | null }): JSX.Element {
    return (
        <Box>
            <Text fz="xs" tt="uppercase" c="dimmed">Summary</Text>
            <Group align="flex-start" gap="md">
                <Box style={{ flex: 1 }}>
                    <Text fz="xs" c="red">Было</Text>
                    <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>{before ?? '(нет)'}</Text>
                </Box>
                <Box style={{ flex: 1 }}>
                    <Text fz="xs" c="green">Стало</Text>
                    <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>{after ?? '(нет)'}</Text>
                </Box>
            </Group>
        </Box>
    );
}

function HealthDeltaCard({ delta }: { delta: RepoDiff['healthDeltas'][number] }): JSX.Element {
    return (
        <Card withBorder padding="sm">
            <Group justify="space-between" mb="xs">
                <Text fw={600}>{delta.title}</Text>
                <Group gap="xs">
                    {delta.added.length   > 0 && <Badge color="red"   size="sm">+{delta.added.length} новых</Badge>}
                    {delta.removed.length > 0 && <Badge color="green" size="sm">-{delta.removed.length} решено</Badge>}
                </Group>
            </Group>
            {delta.added.length > 0 && (
                <Box mb="xs">
                    <Text fz="xs" tt="uppercase" c="dimmed">Новое</Text>
                    {delta.added.map((f, i) => (
                        <Text key={i} fz="sm">⚠ {f.entity.type} «{f.entity.name}»: {f.message}</Text>
                    ))}
                </Box>
            )}
            {delta.removed.length > 0 && (
                <Box>
                    <Text fz="xs" tt="uppercase" c="dimmed">Решено</Text>
                    {delta.removed.map((f, i) => (
                        <Text key={i} fz="sm">✓ {f.entity.type} «{f.entity.name}»: {f.message}</Text>
                    ))}
                </Box>
            )}
        </Card>
    );
}

/**
 * Регистрация контекста глобального чата для страницы сравнения.
 * Отдельный компонент, чтобы не плодить условные хуки в ComparePage.
 */
function CompareChatContext({ active, owner, repo, gitRef, path, base, head, baseRef, headRef }: {
    active: boolean;
    owner?: string; repo?: string; gitRef?: string; path?: string;
    base?: string | null; head?: string | null; baseRef?: string; headRef?: string;
}): null {
    useEffect(() => {
        if (!active || !owner || !repo || !base || !head) return;
        registerChatContext({
            owner, repo, gitRef, path,
            pageContext: {
                view:    'compare',
                baseSha: base,
                headSha: head,
                baseRef,
                headRef,
                subpath: path,
            } satisfies ChatPageContext,
        });
    });
    return null;
}
